<template>
  <main class="px-4 bg-white">
    <div class="py-8 max-w-screen-xl mx-auto">
      <p v-if="!user.super_admin" class="text-sm text-gray-700">
        La création de courtiers est réservée aux super admins.
      </p>
      <form v-else class="space-y-8 divide-y divide-gray-200" @submit.prevent="submit">
        <div class="space-y-8 divide-y divide-gray-200 sm:space-y-5">
          <div>
            <div>
              <h3 class="text-lg leading-6 font-medium text-gray-900">
                Nouveau courtier
              </h3>
              <p class="mt-1 text-sm text-gray-500">
                Le courtier reprend l'agence, le statut admin et les accès aux promotions du courtier choisi.
              </p>
            </div>

            <div class="mt-6 sm:mt-5 space-y-6 sm:space-y-5">
              <div
                v-for="field in fields"
                :key="field.name"
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  :for="field.name"
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  {{ field.label }}
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2">
                  <input
                    v-model="broker[field.name]"
                    :type="field.type"
                    :name="field.name"
                    :id="field.name"
                    :autocomplete="field.type === 'password' ? 'new-password' : 'off'"
                    class="max-w-lg block w-full shadow-sm form-input"
                  />
                </div>
              </div>

              <div
                class="sm:grid sm:grid-cols-3 sm:gap-4 sm:items-start sm:border-t sm:border-gray-200 sm:pt-5"
              >
                <label
                  class="block text-sm font-medium text-gray-700 sm:mt-px sm:pt-2"
                >
                  Reprendre les droits de
                </label>
                <div class="mt-1 sm:mt-0 sm:col-span-2" v-if="brokers.length">
                  <picture-select
                    :searchProps="['full_name', 'email']"
                    :collection="brokers"
                    textProp="full_name"
                    imgProp="profile_picture"
                    defaultText="Courtier"
                    valueProp="id"
                    :value="broker.source_broker_id"
                    @change="val => { broker.source_broker_id = val; }"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="pt-5">
          <div class="flex justify-end">
            <button
              @click="$router.go(-1)"
              type="button"
              class="bg-white py-2 px-8 border border-gray-300 shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Annuler
            </button>
            <button
              type="submit"
              :disabled="!isValid"
              :class="submitButtonClass"
              class="ml-3 inline-flex justify-center py-2 px-8 border border-transparent shadow-sm text-sm font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Créer le courtier
            </button>
          </div>
        </div>
      </form>
    </div>
  </main>
</template>

<script>
import brokersApi from "@/api/brokers";
import { mapGetters } from "vuex";

import PictureSelect from "@/components/Forms/PictureSelect";

export default {
    name: "BrokerAdd",

    components: {
        PictureSelect
    },

    data() {
        return {
            broker: this.emptyBroker(),
            fields: [
                { name: "first_name", label: "Prénom", type: "text" },
                { name: "last_name", label: "Nom", type: "text" },
                { name: "email", label: "Email", type: "email" },
                { name: "phone", label: "Téléphone", type: "text" },
                { name: "password", label: "Mot de passe (8 caractères min.)", type: "password" },
            ],
            brokers: [],
            isDisabled: false,
        };
    },

    mounted() {
        if (this.user.super_admin) {
            brokersApi.get().then(({ data }) => {
                this.brokers = data.data;
            });
        }
    },

    computed: {
        ...mapGetters("auth", ["user"]),

        isValid() {
            if (this.isDisabled)
                return false;
            const fields = ["first_name", "last_name", "email", "password", "source_broker_id"];
            for (const field of fields) {
                if ([null, ""].includes(this.broker[field])) {
                    return false;
                }
            }
            return this.broker.password.length >= 8;
        },

        submitButtonClass() {
            return this.isValid
                ? "bg-blue-400 hover:bg-blue-600 text-gray-100 cursor-pointer"
                : "bg-gray-300 text-gray-700 cursor-not-allowed";
        }
    },

    methods: {
        emptyBroker() {
            return {
                first_name: "",
                last_name: "",
                email: "",
                phone: "",
                password: "",
                source_broker_id: null,
            };
        },

        async submit() {
            if (!this.isValid) return;

            this.isDisabled = true;
            this.$store.commit("loader/SET_LOADING", true);

            try {
                const { data } = await brokersApi.create(this.broker);
                const nbPromotions = data.data.promotions.length;
                this.$toast.open({
                    message: `${data.data.full_name} a été créé avec accès à ${nbPromotions} promotion${nbPromotions > 1 ? "s" : ""}`,
                    type: "success"
                });
                this.broker = this.emptyBroker();
                this.brokers.push(data.data);
            } catch (error) {
                const errors = error.response && error.response.data && error.response.data.errors;
                this.$toast.open({
                    message: errors ? Object.values(errors)[0][0] : "Une erreur est survenue",
                    type: "error"
                });
            }

            this.$store.commit("loader/SET_LOADING", false);
            this.isDisabled = false;
        },
    },

    watch: {
        $route: {
            immediate: true,
            handler() {
                document.title = "E-Promotion | Nouveau courtier";
            }
        },
    },
};
</script>
